import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SOCIALS } from "@/lib/socials";
import { MapPin, Mail, Phone, BadgeCheck } from "lucide-react";
import { LOGO_WHITE, PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";

const SocialIcons = () => {
  const [links, setLinks] = useState({});
  useEffect(() => { api.get("/settings/social").then((r) => setLinks(r.data)).catch(() => {}); }, []);
  const active = SOCIALS.filter((s) => links[s.key]);
  if (!active.length) return null;
  return (
    <ul data-testid="footer-socials" className="mt-6 flex items-center gap-3">
      {active.map(({ key, label, Icon }) => (
        <li key={key}>
          <a href={links[key]} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} data-testid={`footer-social-${key}`}
            className="w-10 h-10 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:bg-[#E0C173] hover:border-[#E0C173] hover:text-[#1B2A4A] hover:-translate-y-0.5 transition-all">
            <Icon className="w-4 h-4" />
          </a>
        </li>
      ))}
    </ul>
  );
};

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0D1629] text-white pb-20 sm:pb-0">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/"><img src={LOGO_WHITE} alt="PROAC Transmission" data-testid="footer-logo" className="w-52 h-auto object-contain" /></Link>
          <p className="mt-6 text-white/60 max-w-md leading-relaxed text-sm">
            Cabinet d'accompagnement à la transmission d'entreprise. Nous mettons en relation
            cédants et repreneurs avec rigueur, confidentialité et expertise.
          </p>
          <div data-testid="footer-carte-pro" className="mt-6 max-w-md border-l-2 border-[#E0C173] pl-4 text-xs text-white/60 leading-relaxed">
            <div className="flex items-center gap-2 text-white/80 font-medium uppercase tracking-wider"><BadgeCheck className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} /> Carte professionnelle</div>
            <p className="mt-1">Transactions sur immeubles et fonds de commerce (loi n° 70-9 du 2 janvier 1970, dite loi Hoguet)</p>
            <p>N° <span data-testid="footer-carte-pro-number" className="text-white/80">CPI 1310 2026 000 000 074</span> · valable jusqu'au 10/05/2029</p>
            <p>Délivrée par la CCI de Marseille-Provence · SIREN 504 980 822</p>
          </div>
          <SocialIcons />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#E0C173] mb-4">Navigation</p>
          <ul className="space-y-3 text-sm text-white/70">
            <li><Link to="/" className="hover:text-white">Accueil</Link></li>
            <li><Link to="/catalogue" className="hover:text-white">Entreprises à céder</Link></li>
            <li><Link to="/entreprises-vendues" data-testid="footer-sold-link" className="hover:text-white">Nos dernières cessions</Link></li>
            <li><Link to="/rdv-decouverte" data-testid="footer-rdv-link" className="text-[#E0C173] hover:text-white">RDV découverte</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-white">Contact</Link></li>
            <li><Link to="/conseils" className="hover:text-white">Conseils</Link></li>
            <li><Link to="/inscription?role=seller" className="hover:text-white">Vendre mon entreprise</Link></li>
            <li><Link to="/inscription?role=buyer" className="hover:text-white">Devenir acheteur</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#E0C173] mb-4">Contact</p>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} /> 55 rue Pythagore, 13290 Aix-en-Provence</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" strokeWidth={1.5} /> contact@proac-transmission.fr</li>
          </ul>
          <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone" className="group mt-6 inline-flex items-center gap-3 bg-[#E0C173] text-[#0D1629] pl-3 pr-5 py-3 rounded-full hover:bg-white transition-colors">
            <span className="w-9 h-9 rounded-full bg-[#0D1629] text-[#E0C173] flex items-center justify-center group-hover:scale-110 transition-transform"><Phone className="w-4 h-4" strokeWidth={2} /></span>
            <span className="leading-tight"><span className="block text-[10px] uppercase tracking-widest opacity-70">Appelez-nous</span><span className="block text-lg font-bold tracking-wide">{PHONE_DISPLAY}</span></span>
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-6 sm:px-8 sm:pr-64 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/40">
        <p>© {new Date().getFullYear()} PROAC Transmission — Tous droits réservés.</p>
        <ul data-testid="footer-legal-links" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <li><Link to="/mentions-legales" data-testid="footer-mentions-link" className="hover:text-white transition-colors">Mentions légales</Link></li>
          <li><Link to="/politique-de-confidentialite" data-testid="footer-confidentialite-link" className="hover:text-white transition-colors">Politique de confidentialité</Link></li>
        </ul>
      </div>
    </footer>
  );
}
